import { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, Calendar, Check, Clock, Heart, MapPin, MessageCircle, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCatalog } from '../data/catalog';
import { trackView } from '../utils/analytics';
import { waConfigured, waLink } from '../config/whatsapp';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { useCart } from '../utils/cart';
import { useCurrency } from '../utils/currency';
import { Reveal } from '../components/Reveal';
import { PriceOnRequest } from '../components/PriceOnRequest';
import { ExternalDistributionLink } from '../components/ExternalDistributionLink';
import { EcoAssistContextButton } from '../components/EcoAssistContextButton';
import { useCuratedCopy } from '../data/curatedArchitecture';

const List = ({ items, icon: Icon = Check, cls = 'text-eco-green' }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-eco-ink/70">
        <Icon size={15} className={`mt-0.5 shrink-0 ${cls}`} /> {item}
      </li>
    ))}
  </ul>
);

const Block = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-eco-ink/10 bg-white p-6 md:p-7">
    <h2 className="text-xl leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{title}</h2>
    <div className="eco-body mt-4">{children}</div>
  </div>
);

export default function PackageDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const curated = useCuratedCopy(lang);
  const { pathname } = useLocation();
  const { packageBySlug } = useCatalog();
  const { add: addToCart, includes: cartIncludes } = useCart();
  const { convert } = useCurrency();
  const p = packageBySlug(slug);
  const isTours = pathname.startsWith('/tours');
  const listPath = isTours ? '/tours' : '/full-packages';
  // Approved public base prices are EUR; show them converted in the visitor currency as estimates.
  const pkgConverted = p && (p.base_price_currency || 'EUR') === 'EUR' ? convert(p.base_price) : null;
  const pkgBasePrice = pkgConverted ? pkgConverted.amount : p?.base_price;
  const pkgCurrency = pkgConverted ? pkgConverted.currency : (p?.base_price_currency || 'EUR');

  useSEO(p ? `${p.name} | Eco Collection` : 'Pacote | Eco Collection', p ? p.overview.slice(0, 150) : 'Pacote Eco Collection');

  useEffect(() => {
    if (p?.slug) trackView('package', p.slug);
  }, [p?.slug]);

  if (!p) return <Navigate to={listPath} replace />;

  return (
    <div data-testid={`package-page-${slug}`}>
      <section className="relative overflow-hidden bg-eco-deep pt-28 md:pt-32">
        <img src={p.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-eco-deep/60 to-eco-deep" />
        <div className="relative mx-auto max-w-6xl px-5 pb-12 md:px-8">
          <Link to={listPath} data-testid="package-back-link" className="inline-flex items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-eco-gold">
            <ArrowLeft size={15} /> {t('back')}
          </Link>
          <Reveal className="mt-6">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-eco-gold">{p.destination} · {p.style}</p>
            <h1 className="eco-page-title mt-3 max-w-3xl text-white">{p.name}</h1>
            <p className="mt-4 flex flex-wrap items-center gap-4 text-sm leading-[1.5] font-normal text-white/70">
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-eco-gold" /> {p.destination}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-eco-gold" /> {p.duration}{/^\d+\s/.test(p.duration) ? ` / ${parseInt(p.duration, 10) - 1} ${t('detail.nights')}` : ''}</span>
              <span className="flex items-center gap-1.5"><Users size={14} className="text-eco-gold" /> {p.idealFor}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-eco-cream py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 md:px-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="max-w-[720px] space-y-6">
            <Reveal>
              <div className="overflow-hidden rounded-2xl">
                <img src={p.img} alt={p.name} className="h-72 w-full object-cover md:h-96" />
              </div>
            </Reveal>
            <Reveal><Block title={t('detail.overview')} testid="package-overview"><p className="leading-relaxed text-eco-ink/70">{p.overview}</p></Block></Reveal>
            <Reveal><Block title={t('detail.highlights')} testid="package-highlights"><List items={p.highlights} /></Block></Reveal>
            <Reveal>
              <Block title={t('detail.itinerary')} testid="package-itinerary">
                <ol className="space-y-3.5">
                  {p.itinerary.map((d, i) => (
                    <li key={i} className="flex items-start gap-3.5">
                      <span className="grid h-9 w-14 shrink-0 place-items-center rounded-lg bg-eco-green text-xs font-bold text-eco-gold">{d.day}</span>
                      <p className="pt-2 text-sm text-eco-ink/70">{d.text}</p>
                    </li>
                  ))}
                </ol>
              </Block>
            </Reveal>
            <Reveal>
              <div className="grid gap-6 sm:grid-cols-2">
                <Block title={t('detail.accommodation')}><p className="text-sm text-eco-ink/70">{p.accommodationNote}</p></Block>
                <Block title={t('nav.transfers')}><p className="text-sm text-eco-ink/70">{p.transfersNote}</p></Block>
                <Block title={t('detail.meals')}><p className="text-sm text-eco-ink/70">{p.meals}</p></Block>
                <Block title={t('page.experiences.k')}><List items={p.experiencesIncluded} /></Block>
              </div>
            </Reveal>
            <Reveal>
              <div className="grid gap-6 sm:grid-cols-2">
                <Block title={t('detail.included')} testid="package-included"><List items={p.included} /></Block>
                <Block title={t('detail.notIncluded')}><List items={p.notIncluded} icon={X} cls="text-red-400" /></Block>
              </div>
            </Reveal>
            {p.optional?.length > 0 && (
              <Reveal><Block title={t('detail.optional')}><List items={p.optional} icon={ArrowRight} cls="text-eco-gold" /></Block></Reveal>
            )}
            <Reveal><Block title={t('detail.info')}><List items={p.important} icon={Clock} cls="text-eco-gold" /></Block></Reveal>
          </div>

          <div className="space-y-6">
            <Reveal delay={0.1} className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm">
                <p className="eco-field-label mb-4">{t('pkg.information')}</p>
                <dl className="grid grid-cols-2 gap-4">
                  <div><dt className="eco-field-label">{t('filter.destination')}</dt><dd className="mt-1 text-sm font-medium text-eco-ink">{p.destination}</dd></div>
                  <div><dt className="eco-field-label">{t('filter.duration')}</dt><dd className="mt-1 text-sm font-medium text-eco-ink">{p.duration}{/^\d+\s/.test(p.duration) ? ` / ${parseInt(p.duration, 10) - 1} ${t('detail.nights')}` : ''}</dd></div>
                  <div><dt className="eco-field-label">{t('pkg.style')}</dt><dd className="mt-1 text-sm font-medium text-eco-ink">{p.style}</dd></div>
                  <div><dt className="eco-field-label">{t('pkg.idealfor')}</dt><dd className="mt-1 text-sm font-medium text-eco-ink">{p.idealFor}</dd></div>
                </dl>
                <div className="mt-6 border-t border-eco-ink/10 pt-5">
                  <PriceOnRequest
                    testid="package-price-state"
                    basePrice={pkgBasePrice}
                    currency={pkgCurrency}
                    showCta
                    ctaLabel={t('cta.addPackageTrip')}
                    to={`/request-proposal?package=${encodeURIComponent(p.slug)}`}
                  />
                  <button
                    type="button"
                    data-testid="package-add-trip"
                    onClick={() => {
                      const result = addToCart({ type: 'package', slug: p.slug, name: p.name, destination: p.destination, priceEur: (p.base_price_currency || 'EUR') === 'EUR' ? p.base_price : null });
                      if (!result.added) toast(t('cart.duplicate'), { description: p.name });
                      else toast.success(t('tours.added'), { description: p.name });
                    }}
                    className="eco-btn-text mt-2 min-h-11 w-full justify-center"
                  >
                    <Heart size={15} className={cartIncludes('package', p.slug) ? 'fill-eco-gold text-eco-gold' : ''} /> {t('tours.add')}
                  </button>
                  <Link
                    to={`/plan-my-trip?package=${p.slug}`}
                    data-testid="package-customize-cta"
                    className="eco-btn-text mt-2 min-h-11 w-full justify-center"
                  >
                    {t('cta.customize')}
                  </Link>
                  <EcoAssistContextButton context="journey" className="mt-2 w-full justify-center">{curated.assist.journey}</EcoAssistContextButton>
                  <ExternalDistributionLink links={p.external_distribution} testid="package-external-distribution" />
                </div>
                <div className="mt-6 border-t border-eco-ink/10 pt-5" data-testid="package-services">
                  <p className="eco-field-label mb-3">{t('pkg.services')}</p>
                  <ul className="space-y-2 text-sm text-eco-ink/70">
                    {p.accommodationNote && <li className="flex items-start gap-2"><BadgeCheck size={14} className="mt-0.5 shrink-0 text-eco-green" /> {p.accommodationNote}</li>}
                    {p.experiencesIncluded?.length > 0 && <li className="flex items-start gap-2"><BadgeCheck size={14} className="mt-0.5 shrink-0 text-eco-green" /> {p.experiencesIncluded.join(', ')}</li>}
                    {p.transfersNote && <li className="flex items-start gap-2"><BadgeCheck size={14} className="mt-0.5 shrink-0 text-eco-green" /> {p.transfersNote}</li>}
                    <li className="flex items-start gap-2"><BadgeCheck size={14} className="mt-0.5 shrink-0 text-eco-green" /> {t('pkg.support')} Eco Collection</li>
                  </ul>
                </div>
                {p.partner && (
                  <p className="mt-5 flex items-center gap-1.5 text-xs text-eco-ink/55">
                    <BadgeCheck size={14} className="text-eco-green" /> {t('detail.operator')}: {p.partner}
                  </p>
                )}
                {waConfigured && (
                  <a
                    href={waLink(t('wa.exp', { exp: p.name, dest: p.destination }))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="package-whatsapp-cta"
                    className="mt-3 flex min-h-12 items-center justify-center gap-2 rounded-[10px] bg-[#25D366] px-6 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#1eb85a] md:min-h-11"
                  >
                    <MessageCircle size={16} /> {t('cta.whatsapp')}
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
